import React, { useState, useMemo, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDataProvider, useNotify, useGetList, useRecordContext, Edit, useRefresh } from 'react-admin';
import {
  Box, Typography, Card, CardContent, Stack, Button, TextField,
  Autocomplete, Select, MenuItem, FormControl, InputLabel,
  Table, TableHead, TableRow, TableCell, TableBody, IconButton,
  Divider, CircularProgress, Chip, Dialog, DialogTitle,
  DialogContent, DialogActions, RadioGroup, FormControlLabel, Radio,
  Collapse, Tooltip,
} from '@mui/material';
import AddIcon      from '@mui/icons-material/Add';
import DeleteIcon   from '@mui/icons-material/Delete';
import SaveIcon     from '@mui/icons-material/Save';
import ReceiptIcon  from '@mui/icons-material/Receipt';
import CloseIcon    from '@mui/icons-material/Close';
import PaymentIcon    from '@mui/icons-material/Payment';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import CurrencyExchangeIcon from '@mui/icons-material/CurrencyExchange';
import ProfitSummary from './ProfitSummary';
import EntityTasksPanel from '../tasks/EntityTasksPanel';
import AddPaymentForm from './AddPaymentForm';
import ReturnDialog from './ReturnDialog';
import SupplierAllocationSelector from './SupplierAllocationSelector';
import SupplierFulfillmentPanel from './SupplierFulfillmentPanel';
import WebsiteOrderPanel from './WebsiteOrderPanel';
import { saveLineAllocation } from './saveLineAllocation';
import { lineUnchanged } from './lineUnchanged';
import { PAY_STATUS_CHOICES } from './paymentChoices';
import authFetch, { responseError } from '../utils/authFetch';
import useUsdRate from '../utils/useUsdRate';
import {
  card, btn, ORDER_STATUS, PAYMENT_STATUS, ORDER_PAYMENT_STATUS, PAYMENT_METHOD_LABELS, inputSx, badgeStyle,
  ORDER_ORIGIN, WEBSITE_APPROVAL_STATUS, WEBSITE_SHIPPING_REVIEW_STATE,
} from '../styles/ds';

const STOCK_DEDUCTING_STATUSES = ['shipped', 'delivered'];

const STATUS_CHOICES = Object.entries(ORDER_STATUS).map(([id, { label }]) => ({ id, label }));


const fmtIls = v => Number(v || 0).toLocaleString('he-IL', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const fmtUsd = v => Number(v || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const fmtDate = iso => iso ? new Date(iso).toLocaleDateString('he-IL') : '—';

const EMPTY_LINE = { _key: 0, id: null, variant: null, quantity: 1, price_ils: '', price_usd: '', cost_ils: '', cost_usd: '', isNew: true, allocationValue: null };
let lineKey = 1;

// crypto.randomUUID() needs a secure context (HTTPS or localhost) and isn't
// guaranteed to exist — fall back to a manual UUIDv4 so a plain-HTTP
// deployment doesn't break every save.
const genIdempotencyKey = () => (
  typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
    ? crypto.randomUUID()
    : 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, c => {
        const r = (Math.random() * 16) | 0;
        return (c === 'x' ? r : ((r & 0x3) | 0x8)).toString(16);
      })
);

// ── Main OrderEdit ─────────────────────────────────────────────────────────────
const OrderEditContent = () => {
  const record   = useRecordContext();
  const dp       = useDataProvider();
  const notify   = useNotify();
  const navigate = useNavigate();
  const refresh  = useRefresh();

  const [customer, setCustomer]     = useState(null);
  const [status, setStatus]         = useState('pending');
  const [lines, setLines]           = useState([]);
  const [deletedIds, setDeletedIds] = useState([]);
  const [saving, setSaving]         = useState(false);
  const [loaded, setLoaded]         = useState(false);
  const [addPaymentOpen, setAddPaymentOpen] = useState(false);
  const [usdRate, setUsdRate, saveUsdRate] = useUsdRate();

  // Receipt dialog state
  const [receiptOpen, setReceiptOpen]         = useState(false);
  const [receiptCurrency, setReceiptCurrency] = useState('ILS');
  const [receiptLang, setReceiptLang]         = useState('he');
  const [receiptLoading, setReceiptLoading]   = useState(false);

  // Return dialog state — which saved item is being returned
  const [returnItem, setReturnItem] = useState(null);

  // Stale-conflict recovery — set when a save is rejected with 409/'stale'
  // (the order changed since it was loaded). Retry reloads via refresh(),
  // which also refreshes the expected_status captured below.
  const [staleConflict, setStaleConflict] = useState(false);

  const { data: customers = [], isLoading: cusLoading } = useGetList('customers', {
    pagination: { page: 1, perPage: 500 },
    sort: { field: 'last_name', order: 'ASC' },
  });
  // Bypass react-admin cache — fetch variants fresh every time. Re-run after
  // a save too (not just on mount): lineAvailable's shortfall math adds this
  // line's OWN saved quantity back into `reserved` (so it isn't double-
  // counted against itself) — if a quantity CHANGE is saved but `reserved`
  // stays the stale pre-save number, the two terms cancel out algebraically
  // (qty == the new savedQuantity, which is exactly what got added back),
  // leaving a shortfall that never reflects the quantity actually just
  // saved — see the "ייצור ומנות quantity doesn't update" bug report.
  const [variants, setVariants]     = useState([]);
  const [varLoading, setVarLoading] = useState(true);
  const loadVariants = useCallback(() => {
    setVarLoading(true);
    return authFetch('/variants?_start=0&_end=9999&_sort=name&_order=ASC')
      .then(r => r.json())
      .then(d => { setVariants(Array.isArray(d) ? d : []); setVarLoading(false); })
      .catch(() => setVarLoading(false));
  }, []);
  useEffect(() => { loadVariants(); }, [loadVariants]);
  const {
    data: existingItems = [],
    isLoading: itemsLoading,
    refetch: refetchItems,
  } = useGetList('order_items', {
    filter: { order_id: record?.id },
    pagination: { page: 1, perPage: 200 },
    sort: { field: 'id', order: 'ASC' },
  });

  // Saved supplier allocation plan per existing item — fetched once so the
  // lines effect below can hydrate each line's allocationValue from it.
  const [allocationsByItemId, setAllocationsByItemId] = useState({});
  const [allocationsLoaded, setAllocationsLoaded]     = useState(false);
  useEffect(() => {
    if (itemsLoading || allocationsLoaded) return;
    if (existingItems.length === 0) { setAllocationsLoaded(true); return; }
    Promise.all(existingItems.map(item =>
      authFetch(`/order_items/${item.id}/supplier-allocations`).then(r => r.json())
        .then(d => [item.id, d]).catch(() => [item.id, null])
    )).then(pairs => {
      setAllocationsByItemId(Object.fromEntries(pairs));
      setAllocationsLoaded(true);
    });
  }, [itemsLoading, allocationsLoaded, existingItems]);
  const {
    data: payments = [],
    isLoading: paymentsLoading,
    refetch: refetchPayments,
  } = useGetList('payments', {
    filter: { order_id: record?.id },
    pagination: { page: 1, perPage: 200 },
    sort: { field: 'id', order: 'ASC' },
  });

  const variantOptions  = useMemo(
    () => variants.map(v => ({ id: v.id, label: v.name || `וריאנט #${v.id}`, price_ils: v.price_ils, price_usd: v.price_usd, cost_ils: v.cost_ils, cost_usd: v.cost_usd, unit: v.unit, quantity: v.quantity, reserved: v.reserved, isFinishedProduct: v.product_is_finished_product })),
    [variants]
  );
  const customerOptions = useMemo(
    () => customers.map(c => ({ id: c.id, label: `${c.first_name} ${c.last_name}` })),
    [customers]
  );

  useEffect(() => {
    if (!record || loaded || itemsLoading || varLoading || cusLoading || !allocationsLoaded) return;
    setStatus(record.status || 'pending');
    const cust = customerOptions.find(c => c.id === record.customer_id);
    if (cust) setCustomer(cust);
    const mapped = existingItems.map(item => {
      const varOpt = variantOptions.find(v => v.id === item.variant_id) || null;
      const saved  = allocationsByItemId[item.id];
      const price_ils = item.price_at_purchase || varOpt?.price_ils || '';
      const price_usd = item.price_usd_at_purchase || varOpt?.price_usd || '';
      // Already resolved from the saved plan — the selector won't re-default it.
      // requested_quantity comes back from a numeric(10,3) column as a
      // string like "65.000" — normalize so the quantity field shows a
      // plain number instead of the raw DB formatting.
      const allocationValue = saved
        ? { mode: saved.allocation_mode, allocations: saved.allocations.map(a => ({ ...a, requested_quantity: Number(a.requested_quantity) })) }
        : { mode: 'automatic', allocations: [] };
      return {
        _key: lineKey++, id: item.id, variant: varOpt, quantity: item.quantity,
        savedQuantity: item.quantity,
        price_ils, price_usd,
        cost_ils: item.unit_cost_ils_snapshot ?? varOpt?.cost_ils ?? '',
        cost_usd: item.unit_cost_usd_snapshot ?? varOpt?.cost_usd ?? '',
        isNew: false,
        allocationValue,
        // Snapshot of what's actually on the server, so handleSave can skip
        // re-PUTting a line nothing changed about — needed once the order is
        // already shipped/delivered, where the backend's order-editability
        // guard rejects any order_items/allocation write, even a no-op one
        // (e.g. just moving the order on to "delivered" with no item edits).
        _original: {
          variantId: item.variant_id, quantity: item.quantity, price_ils, price_usd,
          allocation: JSON.stringify(allocationValue),
        },
      };
    });
    setLines(mapped.length > 0 ? mapped : [{ ...EMPTY_LINE, _key: lineKey++ }]);
    setLoaded(true);
  }, [record, existingItems, itemsLoading, varLoading, cusLoading, allocationsLoaded, allocationsByItemId, loaded, customerOptions, variantOptions]);

  const setLine = (key, patch) => setLines(ls => ls.map(l => l._key === key ? { ...l, ...patch } : l));

  const pickVariant = (key, variantOpt) => {
    if (!variantOpt) { setLine(key, { variant: null, price_ils: '', price_usd: '', cost_ils: '', cost_usd: '', allocationValue: null }); return; }
    // A different variant means its supplier sources are different too —
    // let the selector re-derive (and re-display) a fresh default.
    setLine(key, { variant: variantOpt, price_ils: variantOpt.price_ils ?? '', price_usd: variantOpt.price_usd ?? '', cost_ils: variantOpt.cost_ils ?? '', cost_usd: variantOpt.cost_usd ?? '', allocationValue: null });
    // This customer's own last price for this product (from a completed
    // order) always wins over the product's current global price — the
    // global default above is only what a brand-new customer would see.
    if (customer) {
      authFetch(`/order_items/last-price?customer_id=${customer.id}&variant_id=${variantOpt.id}`)
        .then(r => r.json())
        .then(last => {
          if (!last) return;
          setLine(key, {
            price_ils: last.price_at_purchase ?? '',
            price_usd: last.price_usd_at_purchase ?? '',
          });
        })
        .catch(() => {});
    }
  };

  const addLine    = () => setLines(ls => [...ls, { ...EMPTY_LINE, _key: lineKey++ }]);
  const removeLine = line => {
    if (!line.isNew && line.id) setDeletedIds(ids => [...ids, line.id]);
    setLines(ls => ls.filter(l => l._key !== line._key));
  };

  const totals = useMemo(() => {
    let ils = 0, usd = 0;
    for (const l of lines) {
      const qty = Number(l.quantity) || 0;
      ils += qty * (Number(l.price_ils) || 0);
      usd += qty * (Number(l.price_usd) || 0);
    }
    return { ils, usd };
  }, [lines]);

  // Website orders (docs/website/ Phase B): lines, customer and totals come
  // from the approved website revision — the backend refuses CRM edits to
  // them — so this screen shows the stored totals (shipping/discounts
  // included; NULL = still pending review, never shown as 0) and only lets
  // the status change.
  const isWebsite = record?.origin === 'website';
  const displayTotals = isWebsite
    ? {
        ils: Number(record.total_price_ils) || 0,
        usd: record.total_price_usd === null || record.total_price_usd === undefined ? null : Number(record.total_price_usd),
      }
    : totals;

  // Stock available for a line right now — `reserved` (from /variants) sums
  // this order's own line too when the order is still in a stock-reserving
  // status, so subtract that back out or an existing line looks short
  // against its own reservation. Once the order has shipped/delivered, its
  // items are already deducted and settled — a shortage warning no longer
  // means anything for a line that isn't going to be fulfilled again.
  const lineAvailable = (line) => {
    if (!line.variant) return null;
    if (STOCK_DEDUCTING_STATUSES.includes(record.status)) return null;
    const onHand    = Number(line.variant.quantity) || 0;
    const reserved  = Number(line.variant.reserved) || 0;
    // A cancelled order's items were never counted in `reserved` to begin
    // with (see STOCK_RESERVING_STATUSES) — only add its own quantity back
    // for the still-open (pending/processing) statuses that do count it.
    const ownReserved = (!line.isNew && record.status !== 'cancelled') ? Number(line.savedQuantity || 0) : 0;
    return onHand - reserved + ownReserved;
  };

  // The Fetch spec guarantees fetch() only ever rejects with a TypeError for
  // a genuine network failure (offline, DNS, CORS, connection reset) — never
  // for anything else. That's what distinguishes "no HTTP response was ever
  // received, genuinely uncertain outcome" from react-admin's own HttpError
  // (thrown for a definitive non-2xx response, never a TypeError) or any
  // other unexpected client-side bug, which should surface, not be retried.
  const isNetworkError = e => e instanceof TypeError;

  const handleSave = async () => {
    if (!customer) { notify('נא לבחור לקוח', { type: 'warning' }); return; }
    const validLines = lines.filter(l => l.variant && Number(l.quantity) > 0);
    if (!validLines.length) { notify('נא להוסיף לפחות פריט אחד', { type: 'warning' }); return; }
    setSaving(true);
    setStaleConflict(false);

    // One idempotency_key + one frozen payload per save attempt (this call),
    // reused verbatim by the single automatic retry below on a network-level
    // failure — never regenerated or recomputed from possibly-changed state
    // mid-attempt. expected_status is `record.status` — the status as it was
    // when this order was loaded (immutable until refresh() reloads it).
    // Website orders resend their stored customer/totals unchanged — the
    // backend only accepts a status change for them.
    const orderUpdateData = {
      customer_id: isWebsite ? record.customer_id : customer.id, status,
      total_price_ils: isWebsite ? record.total_price_ils : totals.ils,
      total_price_usd: isWebsite ? record.total_price_usd : totals.usd,
      expected_status: record.status,
      idempotency_key: genIdempotencyKey(),
    };
    const saveOrder = () => dp.update('orders', { id: record.id, data: orderUpdateData, previousData: record });

    try {
      // Save items BEFORE the order — a status change may deduct stock, and the
      // deduction must be based on the final items, not the previous ones.
      // lineUnchanged (see its own file) skips re-PUTting a line nothing
      // actually changed about, needed since the backend rejects any
      // order_items/allocation write once the order is already shipped/
      // delivered — even a no-op one.
      if (!isWebsite) await Promise.all([
        ...deletedIds.map(id => dp.delete('order_items', { id })),
        ...validLines.filter(l => !lineUnchanged(l)).map(async l => {
          const data = { order_id: record.id, variant_id: l.variant.id, quantity: Number(l.quantity), price_at_purchase: Number(l.price_ils) || 0, price_usd_at_purchase: Number(l.price_usd) || 0 };
          const saved = l.isNew
            ? await dp.create('order_items', { data })
            : await dp.update('order_items', { id: l.id, data, previousData: {} });
          await saveLineAllocation(saved.data.id, l.allocationValue);
        }),
      ]);

      let saveResult;
      try {
        saveResult = await saveOrder();
      } catch (e) {
        if (!isNetworkError(e)) throw e;
        // Genuinely uncertain outcome — one automatic retry, same key and
        // payload. `saving` is already true for this whole window, so the
        // Save button stays disabled and can't race this with a fresh key.
        saveResult = await saveOrder();
      }

      notify('ההזמנה עודכנה בהצלחה', { type: 'success' });
      const autoFilledFor = saveResult?.data?.auto_filled_usd_price_for;
      if (autoFilledFor?.length) {
        notify(`מחיר ₪ מולא אוטומטית לפי שער $ נוכחי עבור: ${autoFilledFor.join(', ')}`, { type: 'info' });
      }
      setDeletedIds([]);
      // Items/variants must actually be re-fetched (awaited) before letting
      // the mapping effect run again (setLoaded(false)) — useGetList's
      // `isLoading` only reflects the FIRST load, staying false through a
      // later background refetch, so the effect's "!itemsLoading" guard was
      // satisfied instantly and ran with the still-stale cached items from
      // before this save. Once it set loaded=true on that stale pass, the
      // later re-render carrying the fresh items was ignored entirely
      // (the effect bails out whenever loaded is already true) — the exact
      // "I see the before-state, only a page refresh shows after" report.
      await Promise.all([refetchItems(), loadVariants()]);
      setAllocationsLoaded(false);
      setLoaded(false);
      refresh();
    } catch (e) {
      console.error(e);
      if (e?.body?.code === 'stale') {
        setStaleConflict(true);
      } else {
        notify(e?.body?.error || e?.message || 'שגיאה בשמירה', { type: 'error' });
      }
    } finally {
      setSaving(false);
    }
  };

  // Stale-conflict Retry — reloads the current record (and with it, a fresh
  // expected_status for the next save attempt). Any unsaved edits are lost,
  // correctly: they were made against data that's no longer current.
  const handleStaleRetry = async () => {
    setStaleConflict(false);
    await Promise.all([refetchItems(), loadVariants()]);
    setAllocationsLoaded(false);
    setLoaded(false);
    refresh();
  };

  const handleReceipt = async () => {
    setReceiptLoading(true);
    try {
      const res  = await authFetch(`/orders/${record.id}/receipt?currency=${receiptCurrency}&lang=${receiptLang}`);
      if (!res.ok) { notify(await responseError(res, 'שגיאה בהפקת הקבלה'), { type: 'error' }); return; }
      const disposition = res.headers.get('Content-Disposition') || '';
      const match       = disposition.match(/filename="([^"]+)"/);
      const filename    = match ? match[1] : `receipt-${record.id}.pdf`;
      const blob = await res.blob();
      const url  = URL.createObjectURL(blob);
      const a    = document.createElement('a');
      a.href = url; a.download = filename; a.click();
      URL.revokeObjectURL(url);
      notify('הקבלה הופקה ונשלחה במייל', { type: 'success' });
      setReceiptOpen(false);
    } catch {
      notify('שגיאה בהפקת הקבלה', { type: 'error' });
    } finally {
      setReceiptLoading(false);
    }
  };

  const handlePaymentSaved = () => {
    setAddPaymentOpen(false);
    refetchPayments();
    refresh(); // refreshes order record → updates payment_status badge
  };

  if (!record || !loaded) return (
    <Box display="flex" justifyContent="center" py={6}><CircularProgress /></Box>
  );

  const statusColors        = ORDER_STATUS[status] || { bg: '#eee', color: '#333' };
  const paymentStatusMeta   = ORDER_PAYMENT_STATUS[record.payment_status] || ORDER_PAYMENT_STATUS.unpaid;

  // Totals for payment summary
  const paidIls = payments.filter(p => p.status === 'paid' && p.currency === 'ILS').reduce((s, p) => s + Number(p.amount), 0);
  const paidUsd = payments.filter(p => p.status === 'paid' && p.currency === 'USD').reduce((s, p) => s + Number(p.amount), 0);

  // Mirrors the backend's `!wasDeducted && nowDeducting` guard — only block
  // entering shipped/delivered from a non-deducting status when some line
  // has no sale price in either currency (cost snapshots at delivery time
  // would otherwise be meaningless).
  const wasDeducted = STOCK_DEDUCTING_STATUSES.includes(record.status);
  const hasMissingPrice = lines.some(l =>
    l.variant && Number(l.quantity) > 0 && !(Number(l.price_ils) > 0 || Number(l.price_usd) > 0));

  return (
    <Box className="crm-page crm-detail-content" p={3} sx={{ width: '100%', boxSizing: 'border-box' }}>

      {/* ── Header ──────────────────────────────────────────────────────── */}
      <Stack className="crm-header" direction="row" justifyContent="space-between" alignItems="center" mb={3}>
        <Stack className="crm-wrap" direction="row" spacing={1.5} alignItems="center" flexWrap="wrap" gap={1}>
          <Typography variant="h5" fontWeight={700} color="#0d1d3e">הזמנה #{record.id}</Typography>
          {record.customer_order_number && (
            <Typography fontSize={13} color="text.secondary" sx={{ border: '1px solid #e0e0e0', borderRadius: 1.5, px: 1, py: 0.3 }}>
              הזמנה {record.customer_order_number} של הלקוח
            </Typography>
          )}
          <Chip label={STATUS_CHOICES.find(s => s.id === record.status)?.label || record.status}
            sx={{ backgroundColor: statusColors.bg, color: statusColors.color, fontWeight: 700 }} />
          <Chip
            icon={<PaymentIcon sx={{ fontSize: 14, color: `${paymentStatusMeta.color} !important` }} />}
            label={paymentStatusMeta.label}
            sx={{ backgroundColor: paymentStatusMeta.bg, color: paymentStatusMeta.color, fontWeight: 700 }}
          />
        </Stack>
        <Stack direction="row" spacing={1}>
          <Button variant="outlined" startIcon={<ReceiptIcon />} onClick={() => setReceiptOpen(true)}
            sx={{ borderColor: '#c8a96e', color: '#c8a96e', borderRadius: 2,
              '&:hover': { borderColor: '#b8996e', backgroundColor: 'rgba(200,169,110,0.08)' } }}>
            הפק קבלה
          </Button>
          <Button variant="outlined" onClick={() => navigate(-1)}
            sx={{ borderColor: '#ccc', color: '#666', borderRadius: 2 }}>
            חזרה
          </Button>
        </Stack>
      </Stack>

      {/* ── Stale-conflict banner ───────────────────────────────────────── */}
      {staleConflict && (
        <Card sx={{ ...card.section, borderRight: '3px solid #c62828', backgroundColor: 'rgba(198,40,40,0.05)' }}>
          <CardContent sx={{ p: 2 }}>
            <Stack className="crm-header crm-wrap" direction="row" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={1}>
              <Stack direction="row" spacing={1} alignItems="center">
                <WarningAmberIcon sx={{ fontSize: 20, color: '#c62828' }} />
                <Typography fontSize={13} color="#c62828">
                  ההזמנה השתנתה על ידי מישהו אחר מאז שנטענה — יש לרענן ולנסות שוב
                </Typography>
              </Stack>
              <Button size="small" variant="outlined" onClick={handleStaleRetry}
                sx={{ borderColor: '#c62828', color: '#c62828', borderRadius: 2 }}>
                רענן ונסה שוב
              </Button>
            </Stack>
          </CardContent>
        </Card>
      )}

      {/* ── Website order banner (docs/website/ Phase B) ───────────────── */}
      {isWebsite && (
        <Card sx={{ ...card.section, borderRight: '3px solid #c8a96e', backgroundColor: 'rgba(200,169,110,0.08)' }}>
          <CardContent sx={{ p: 2 }}>
            <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" gap={1}>
              <span style={badgeStyle(ORDER_ORIGIN, 'website')}>{ORDER_ORIGIN.website.label}</span>
              {record.website && (
                <>
                  <span style={badgeStyle(WEBSITE_APPROVAL_STATUS, record.website.approval_status)}>
                    {WEBSITE_APPROVAL_STATUS[record.website.approval_status]?.label || record.website.approval_status}
                  </span>
                  <span style={badgeStyle(WEBSITE_SHIPPING_REVIEW_STATE, record.website.shipping_review_state)}>
                    {WEBSITE_SHIPPING_REVIEW_STATE[record.website.shipping_review_state]?.label || record.website.shipping_review_state}
                  </span>
                </>
              )}
              <Typography fontSize={13} color="#0d1d3e">
                הזמנה מהאתר — הפריטים והסכומים מתעדכנים ומאושרים בכרטיס "הזמנה מהאתר" שלמטה, ולא בטופס ההזמנה. כאן ניתן לשנות סטטוס בלבד.
              </Typography>
            </Stack>
          </CardContent>
        </Card>
      )}
      {isWebsite && <WebsiteOrderPanel orderId={record.id} orderStatus={record.status} />}

      {/* ── Customer + Status ────────────────────────────────────────────── */}
      <Card sx={card.section}>
        <CardContent className="crm-card-content" sx={{ p: 3 }}>
          <Typography fontWeight={700} color="#0d1d3e" mb={2}>פרטי הזמנה</Typography>
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2 }}>
            <Autocomplete
              options={customerOptions} loading={cusLoading} value={customer} disabled={isWebsite}
              onChange={(_, v) => setCustomer(v)} getOptionLabel={o => o?.label ?? ''}
              isOptionEqualToValue={(a, b) => a.id === b.id}
              renderInput={params => <TextField {...params} label="לקוח" sx={inputSx} />}
            />
            <FormControl fullWidth>
              <InputLabel sx={{ backgroundColor: '#fff', px: 0.5 }}>סטטוס</InputLabel>
              <Select value={status} onChange={e => setStatus(e.target.value)} sx={inputSx} label="סטטוס">
                {STATUS_CHOICES.map(s => {
                  const blocked = !wasDeducted && STOCK_DEDUCTING_STATUSES.includes(s.id) && hasMissingPrice;
                  return (
                    <MenuItem key={s.id} value={s.id} disabled={blocked}>
                      {s.label}{blocked ? ' (חסר מחיר מכירה)' : ''}
                    </MenuItem>
                  );
                })}
              </Select>
            </FormControl>
          </Box>
        </CardContent>
      </Card>

      {/* ── Order items ──────────────────────────────────────────────────── */}
      <Card sx={card.section}>
        <CardContent className="crm-card-content" sx={{ p: 3 }}>
          <Stack className="crm-header" direction="row" justifyContent="space-between" alignItems="center" mb={2}>
            <Typography fontWeight={700} color="#0d1d3e">פריטי הזמנה</Typography>
            <Stack className="crm-filter-row" direction="row" spacing={1.5} alignItems="center">
              <TextField label="שער $" size="small" type="number" value={usdRate}
                onChange={e => setUsdRate(e.target.value)} onBlur={saveUsdRate}
                inputProps={{ min: 0, step: 0.01, style: { width: 60, textAlign: 'center' } }}
                sx={inputSx} />
              <Button variant="contained" size="small" startIcon={<AddIcon />} onClick={addLine} disabled={isWebsite} sx={btn.primary}>
                הוסף פריט
              </Button>
            </Stack>
          </Stack>

          {itemsLoading ? <CircularProgress size={20} /> : (
            // Website order lines are read-only here (the order panel above
            // revises them): each line field is disabled, while the stock
            // actions (production batch / purchase) stay usable.
            <Box data-mobile-scroll sx={{ overflowX: 'auto' }}><Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ minWidth: 220 }}>מוצר</TableCell>
                  <TableCell sx={{ width: 90 }} align="center">כמות</TableCell>
                  <TableCell sx={{ width: 120 }} align="center">מחיר מכירה ₪</TableCell>
                  <TableCell sx={{ width: 120 }} align="center">מחיר מכירה $</TableCell>
                  <TableCell sx={{ width: 110, color: '#888' }} align="center">עלות ₪</TableCell>
                  <TableCell sx={{ width: 110, color: '#888' }} align="center">עלות $</TableCell>
                  <TableCell sx={{ width: 110 }} align="center">סה"כ ₪</TableCell>
                  <TableCell sx={{ width: 110 }} align="center">סה"כ $</TableCell>
                  <TableCell sx={{ width: 48 }} />
                </TableRow>
              </TableHead>
              <TableBody>
                {lines.map((line, idx) => {
                  const qty       = Number(line.quantity) || 0;
                  const lineIls   = qty * (Number(line.price_ils) || 0);
                  const lineUsd   = qty * (Number(line.price_usd) || 0);
                  const available = lineAvailable(line);
                  const shortfall = available != null ? qty - available : 0;
                  return (
                    <React.Fragment key={line._key}>
                    <TableRow sx={{ backgroundColor: idx % 2 === 0 ? '#fff' : '#faf8f5' }}>
                      <TableCell sx={{ py: 1 }}>
                        <Autocomplete options={variantOptions} loading={varLoading} value={line.variant} disabled={isWebsite}
                          onChange={(_, v) => pickVariant(line._key, v)} getOptionLabel={o => o?.label ?? ''}
                          isOptionEqualToValue={(a, b) => a.id === b.id} size="small"
                          renderInput={params => <TextField {...params} placeholder="חפש מוצר..." sx={{ ...inputSx, minWidth: 200 }} />}
                        />
                        {shortfall > 0 && (
                          // Action lives in one consistent place only — the "מנת ייצור"
                          // button inside the allocation selector below, in both modes —
                          // so this stays a pure information signal, no button of its own.
                          <Stack direction="row" spacing={0.5} alignItems="center" mt={0.5}>
                            <WarningAmberIcon sx={{ fontSize: 14, color: '#c62828' }} />
                            <Typography fontSize={11} color="#c62828">חסר {shortfall} במלאי</Typography>
                          </Stack>
                        )}
                      </TableCell>
                      <TableCell sx={{ py: 1 }} align="center">
                        <TextField type="number" size="small" value={line.quantity} disabled={isWebsite}
                          onChange={e => setLine(line._key, { quantity: e.target.value })}
                          inputProps={{ min: 1, style: { textAlign: 'center' } }} sx={{ ...inputSx, width: 70 }} />
                      </TableCell>
                      <TableCell sx={{ py: 1 }} align="center">
                        <TextField type="number" size="small" value={line.price_ils} disabled={isWebsite}
                          onChange={e => setLine(line._key, { price_ils: e.target.value })}
                          inputProps={{ min: 0, style: { textAlign: 'center' } }} sx={{ ...inputSx, width: 100 }} />
                      </TableCell>
                      <TableCell sx={{ py: 1 }} align="center">
                        <Stack direction="row" spacing={0.3} alignItems="center" justifyContent="center">
                          <TextField type="number" size="small" value={line.price_usd} disabled={isWebsite}
                            onChange={e => setLine(line._key, { price_usd: e.target.value })}
                            inputProps={{ min: 0, style: { textAlign: 'center' } }} sx={{ ...inputSx, width: 90 }} />
                          {Number(line.price_usd) > 0 && Number(usdRate) > 0 && (
                            <Tooltip title={`עדכן מחיר ₪ לפי שער ${usdRate}`} arrow>
                              <IconButton size="small" disabled={isWebsite}
                                onClick={() => setLine(line._key, { price_ils: Math.round(Number(line.price_usd) * Number(usdRate) * 100) / 100 })}>
                                <CurrencyExchangeIcon sx={{ fontSize: 16, color: '#c8a96e' }} />
                              </IconButton>
                            </Tooltip>
                          )}
                        </Stack>
                      </TableCell>
                      <TableCell align="center" sx={{ color: '#888', fontSize: 13 }}>
                        {line.cost_ils !== '' && line.cost_ils != null ? `₪${fmtIls(line.cost_ils)}` : '—'}
                      </TableCell>
                      <TableCell align="center" sx={{ color: '#888', fontSize: 13 }}>
                        {line.cost_usd !== '' && line.cost_usd != null ? `$${fmtUsd(line.cost_usd)}` : '—'}
                      </TableCell>
                      <TableCell align="center" sx={{ fontWeight: 600, color: '#0d1d3e' }}>₪{fmtIls(lineIls)}</TableCell>
                      <TableCell align="center" sx={{ fontWeight: 600, color: '#0d1d3e' }}>${fmtUsd(lineUsd)}</TableCell>
                      <TableCell align="center">
                        <Stack direction="row" spacing={0} justifyContent="center">
                          {!line.isNew && line.id && ['shipped', 'delivered'].includes(record.status) && (
                            <Button size="small" disabled={isWebsite} onClick={() => setReturnItem({
                              id: line.id, quantity: Number(line.quantity) || 1,
                              label: line.variant?.label || `פריט #${line.id}`,
                            })}
                              sx={{ fontSize: 10, color: '#856404', minWidth: 0, px: 0.5 }}>
                              החזרה
                            </Button>
                          )}
                          <IconButton size="small" onClick={() => removeLine(line)} disabled={isWebsite || lines.length === 1} aria-label="הסרת שורה" sx={{ color: '#c62828' }}>
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Stack>
                      </TableCell>
                    </TableRow>
                    {/* Supplier allocation: editable plan while pending; actual
                        fulfillment (SupplierFulfillmentPanel, collapsed by
                        default behind its own toggle) once the order already
                        deducted stock for this item */}
                    {line.variant && (
                      <TableRow>
                        <TableCell colSpan={9} sx={{ py: 0, border: 'none', borderRight: '3px solid #c8a96e', backgroundColor: 'rgba(13,29,62,0.018)' }}>
                          <Box px={2} py={1}>
                            {!line.isNew && line.id && STOCK_DEDUCTING_STATUSES.includes(record.status) ? (
                              <SupplierFulfillmentPanel orderItemId={line.id} unit={line.variant.unit} />
                            ) : (
                              <SupplierAllocationSelector
                                variantId={line.variant.id}
                                quantity={qty}
                                unit={line.variant.unit}
                                excludeOrderItemId={line.isNew ? null : line.id}
                                isFinishedProduct={line.variant.isFinishedProduct}
                                value={line.allocationValue}
                                onChange={v => setLine(line._key, { allocationValue: v })}
                                readOnly={isWebsite}
                                onRequestProductionBatch={(suggestedQty) => navigate('/production_batches/create', {
                                  state: {
                                    finished_variant_id: line.variant.id,
                                    finished_variant_name: line.variant.label,
                                    quantity: suggestedQty,
                                  },
                                })}
                                onRequestPurchase={(suggestedQty) => navigate('/purchases/create', {
                                  state: {
                                    variant_id: line.variant.id,
                                    variant_name: line.variant.label,
                                    quantity: suggestedQty,
                                  },
                                })}
                              />
                            )}
                          </Box>
                        </TableCell>
                      </TableRow>
                    )}
                    </React.Fragment>
                  );
                })}
              </TableBody>
            </Table></Box>
          )}

          <Divider sx={{ my: 2 }} />
          <Stack className="crm-actions" direction="row" justifyContent="flex-end" spacing={3}>
            <Box textAlign="center">
              <Typography variant="caption" color="text.secondary">סה"כ ₪</Typography>
              <Typography fontWeight={700} fontSize={20} color="#0d1d3e">₪{fmtIls(displayTotals.ils)}</Typography>
            </Box>
            <Box textAlign="center">
              <Typography variant="caption" color="text.secondary">סה"כ $</Typography>
              <Typography fontWeight={700} fontSize={20} color="#0d1d3e">
                {displayTotals.usd === null ? 'טרם נקבע' : `$${fmtUsd(displayTotals.usd)}`}
              </Typography>
            </Box>
          </Stack>
        </CardContent>
      </Card>

      {/* ── Payments ─────────────────────────────────────────────────────── */}
      <Card sx={card.section}>
        <CardContent className="crm-card-content" sx={{ p: 3 }}>
          {/* Section header */}
          <Stack className="crm-header" direction="row" justifyContent="space-between" alignItems="center" mb={2}>
            <Stack direction="row" spacing={1.5} alignItems="center">
              <Typography fontWeight={700} color="#0d1d3e">תשלומים</Typography>
              <Chip
                label={paymentStatusMeta.label}
                size="small"
                sx={{ backgroundColor: paymentStatusMeta.bg, color: paymentStatusMeta.color, fontWeight: 700, fontSize: 11 }}
              />
            </Stack>
            <Button
              variant={addPaymentOpen ? 'outlined' : 'contained'}
              size="small"
              startIcon={addPaymentOpen ? <CloseIcon /> : <AddIcon />}
              onClick={() => setAddPaymentOpen(o => !o)}
              sx={addPaymentOpen ? { borderColor: '#c8a96e', color: '#c8a96e', borderRadius: 2 } : btn.primary}
            >
              {addPaymentOpen ? 'ביטול' : 'הוסף תשלום'}
            </Button>
          </Stack>

          {/* Inline add form */}
          <Collapse in={addPaymentOpen} timeout={250} unmountOnExit>
            <AddPaymentForm
              orderId={record.id}
              onSaved={handlePaymentSaved}
              onClose={() => setAddPaymentOpen(false)}
            />
          </Collapse>

          {/* Payment summary strip */}
          {payments.length > 0 && (
            <Box className="crm-form-grid" sx={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))',
              gap: 1.5, mb: 2,
              p: 1.5,
              backgroundColor: 'rgba(13,29,62,0.03)',
              borderRadius: 2,
            }}>
              <Box>
                <Typography variant="caption" color="text.secondary">שולם ₪</Typography>
                <Typography fontWeight={700} color="#2e7d32">₪{fmtIls(paidIls)}</Typography>
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary">שולם $</Typography>
                <Typography fontWeight={700} color="#1565c0">${fmtUsd(paidUsd)}</Typography>
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary">יתרה ₪</Typography>
                <Typography fontWeight={700} color={displayTotals.ils - paidIls > 0 ? '#856404' : '#2e7d32'}>
                  ₪{fmtIls(Math.max(0, displayTotals.ils - paidIls))}
                </Typography>
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary">יתרה $</Typography>
                {displayTotals.usd === null ? (
                  <Typography fontWeight={700} color="#856404">טרם נקבע</Typography>
                ) : (
                  <Typography fontWeight={700} color={displayTotals.usd - paidUsd > 0 ? '#856404' : '#1565c0'}>
                    ${fmtUsd(Math.max(0, displayTotals.usd - paidUsd))}
                  </Typography>
                )}
              </Box>
            </Box>
          )}

          {/* Payments table */}
          {paymentsLoading ? <CircularProgress size={20} /> : payments.length === 0 ? (
            <Typography color="text.secondary" fontSize={13} py={2} textAlign="center">
              אין תשלומים עדיין — לחץ "הוסף תשלום" כדי להוסיף
            </Typography>
          ) : (
            <Box data-mobile-scroll sx={{ overflowX: 'auto' }}><Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>#</TableCell>
                  <TableCell align="right">סכום</TableCell>
                  <TableCell>מטבע</TableCell>
                  <TableCell>אמצעי תשלום</TableCell>
                  <TableCell align="center">סטטוס</TableCell>
                  <TableCell align="center">תאריך</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {payments.map((p, i) => (
                  <TableRow key={p.id} sx={{ backgroundColor: i % 2 === 0 ? '#fff' : '#faf8f5' }}>
                    <TableCell sx={{ fontWeight: 600, color: 'text.secondary', fontSize: 13 }}>#{p.id}</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 700 }}>
                      {p.currency === 'ILS' ? `₪${fmtIls(p.amount)}` : `$${fmtUsd(p.amount)}`}
                    </TableCell>
                    <TableCell sx={{ fontSize: 13 }}>{p.currency}</TableCell>
                    <TableCell sx={{ fontSize: 13 }}>{PAYMENT_METHOD_LABELS[p.payment_method] || p.payment_method}</TableCell>
                    <TableCell align="center">
                      <span style={badgeStyle(PAYMENT_STATUS, p.status)}>
                        {PAY_STATUS_CHOICES.find(s => s.id === p.status)?.label || p.status}
                      </span>
                    </TableCell>
                    <TableCell align="center" sx={{ fontSize: 13, color: 'text.secondary' }}>
                      {fmtDate(p.created_at)}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table></Box>
          )}
        </CardContent>
      </Card>

      {/* ── Tasks for this order ────────────────────────────────────────── */}
      <EntityTasksPanel entityType="order" entityId={record.id} />

      {/* ── Profit summary (manager only) ───────────────────────────────── */}
      <ProfitSummary orderId={record.id} orderStatus={record.status} />

      {/* ── Actions ──────────────────────────────────────────────────────── */}
      <Stack className="crm-actions" direction="row" spacing={2} justifyContent="flex-end">
        <Button variant="outlined" onClick={() => navigate(-1)}
          sx={{ borderColor: '#ccc', color: '#666', borderRadius: 2 }}>
          ביטול
        </Button>
        <Button
          variant="contained"
          startIcon={saving ? <CircularProgress size={16} color="inherit" /> : <SaveIcon />}
          onClick={handleSave} disabled={saving}
          sx={{ ...btn.primaryLg }}>
          שמור שינויים
        </Button>
      </Stack>

      {/* ── Return Dialog ─────────────────────────────────────────────────── */}
      <ReturnDialog
        open={!!returnItem}
        item={returnItem}
        onClose={() => setReturnItem(null)}
        onSaved={async () => {
          await Promise.all([refetchItems(), loadVariants()]);
          setAllocationsLoaded(false);
          setLoaded(false);
          refresh();
        }}
      />

      {/* ── Receipt Dialog ────────────────────────────────────────────────── */}
      <Dialog open={receiptOpen} onClose={() => setReceiptOpen(false)} dir="rtl" maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 700, color: '#0d1d3e' }}>הפקת קבלה</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" mb={2}>
            הקבלה תורד למחשב ותישלח במייל לעסק ולקוח (אם יש מייל).
          </Typography>
          <Typography fontWeight={600} mb={1}>בחר מטבע להצגה:</Typography>
          <RadioGroup value={receiptCurrency} onChange={e => setReceiptCurrency(e.target.value)}>
            <FormControlLabel value="ILS" control={<Radio />} label="שקל ₪" />
            <FormControlLabel value="USD" control={<Radio />} label="דולר $" />
          </RadioGroup>
          <Typography fontWeight={600} mb={1} mt={2}>בחר שפת הקבלה:</Typography>
          <RadioGroup value={receiptLang} onChange={e => setReceiptLang(e.target.value)}>
            <FormControlLabel value="he" control={<Radio />} label="עברית" />
            <FormControlLabel value="en" control={<Radio />} label="English" />
          </RadioGroup>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setReceiptOpen(false)} sx={{ color: '#666' }}>ביטול</Button>
          <Button variant="contained" onClick={handleReceipt} disabled={receiptLoading}
            startIcon={receiptLoading ? <CircularProgress size={16} color="inherit" /> : <ReceiptIcon />}
            sx={btn.primary}>
            הפק והורד
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

const OrderEdit = () => (
  <Edit title=" " actions={false}>
    <OrderEditContent />
  </Edit>
);

export default OrderEdit;
